import { useMutation, useQueryClient } from "@tanstack/react-query";

import { config } from "@/core/lib/app-config";
import { addToast } from "@/core/lib/toast";
import type { InferHeyApiMutationOptions } from "@/core/lib/types";
import {
  uploadHelpCenterMediaMutation,
  deleteMediaMutation,
  getMediaMetadataQueryKey,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

export function useUploadMedia(
  options: InferHeyApiMutationOptions<
    typeof uploadHelpCenterMediaMutation
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...uploadHelpCenterMediaMutation({ requestValidator: undefined }), // Disable Zod validation
    ...options,
    meta: { errorMessage: m.toast_media_uploaded_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getMediaMetadataQueryKey({
          path: { media_id: data!.id },
        }),
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_media_uploaded_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

export function useDeleteMedia(
  options: InferHeyApiMutationOptions<typeof deleteMediaMutation> = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...deleteMediaMutation(),
    ...options,
    meta: { errorMessage: m.toast_media_deleted_failed },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({
        queryKey: getMediaMetadataQueryKey({
          path: { media_id: variables.path.media_id },
        }),
      });

      await options?.onSuccess?.(data, variables, onMutateResult, context);
      addToast({
        title: m.toast_media_deleted_success(),
        severity: "success",
        duration: 2500,
      });
    },
  });
}

// Helper to construct media URL
export function getMediaUrl(mediaId: string) {
  return `${config.backendUrl}/help-center/media/${mediaId}/download`;
}
